<template>
  <el-card
    @click.native.stop
    @mouseover.native="enablePulseClass()"
    @mouseleave.native="disablePulseClass()"
    shadow="hover"
    class="card"
    :body-style="{ padding: '0px' }"
  >
    <div class="card-ct">
      <div class="img" :style="getBackgroundImage">
        <div class="status-ct">
          <div>
            <div v-if="this.$isRole('admin')">
              <el-dropdown
                trigger="click"
                @command="handleCommand"
                @click.native.stop
              >
                <el-button icon="el-icon-more" type="plain" circle />
                <el-dropdown-menu #default>
                  <el-dropdown-item command="edit" icon="el-icon-edit-outline">
                    {{ $t('Gamification_GameCard_Edit') }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    command="regions"
                    icon="el-icon-map-location"
                  >
                    {{ $t('Gamification_GameCard_Region') }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="cardType === 'active'"
                    command="deactivate"
                    icon="el-icon-close"
                  >
                    {{ $t('Gamification_GameCard_Deactivate') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
            <el-tag :class="{ pulse: isPulseEnabled }" v-else>
              <div class="tag-ct">
                <icon :glyph="icon" size="10" />
                <span>{{ status }}</span>
              </div>
            </el-tag>
          </div>
        </div>
      </div>
      <div class="info-ct">
        <p class="game-title">{{ game.name }}</p>
        <p class="game-description">{{ game.description }}</p>
      </div>
    </div>
    <game-region-toggle-dialog
      v-if="isDialogVisible"
      :game="game"
      :regions="game.gameRegions"
      :visible="isDialogVisible"
      :type="cardType"
      :active-regions-for-game="getActiveRegionsForGame(game.id)"
      :active-game-id="activeGameId"
      @close="closeRegionToggleDialog"
      @deactivateGame="deactivateGame"
    />
    <add-game-dialog
      v-if="isEditGameDialogVisible"
      :id="game.id"
      :dialog-visible="isEditGameDialogVisible"
      @close="() => (isEditGameDialogVisible = false)"
    />
  </el-card>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import { $isRole } from '@/core/utils/VueAcl'
import GameRegionToggleDialog from '@/modules/gamification/components/games-hub-dashboard/GameRegionToggleDialog.vue'
import { customConfirm } from '@/core/helpers'
import AddGameDialog from '@/modules/gamification/components/games-hub-dashboard/AddGameDialog.vue'

export default {
  name: 'GameCard',
  components: { AddGameDialog, GameRegionToggleDialog },
  props: {
    game: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    cardType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      pulseClass: false,
      isDialogVisible: false,
      isEditGameDialogVisible: false,
      activeGameId: null
    }
  },
  methods: {
    deactivateGame(gameId) {
      const activeGameToDelete = this.$store.getters.activeGames.find(
        activeGame => activeGame.game_id === gameId
      )
      customConfirm(
        this.$t('Gamification_Deactivate_Game_Confirmation_Title'),
        this.$t('Gamification_Deactivate_Game_Confirmation_Description'),
        this.$t('Gamification_Activate_Game_Confirmation_Confirm'),
        this.$t('Gamification_Activate_Game_Confirmation_Cancel')
      )
        .then(() => {
          RestApi({
            method: 'delete',
            url: `projects/${this.$route.query.project_id}/active-games/${activeGameToDelete.id}`
          })
            .then(async () => {
              this.$message({
                message: this.$t('Gamification_Deactivate_Game_Successful'),
                type: 'success'
              })
              await this.$store.dispatch('fetchActiveGames')
            })
            .catch(() => {
              this.$message({
                message: this.$t('Gamification_Deactivate_Game_Failed'),
                type: 'error'
              })
            })
        })
        .catch(() => {})
    },
    showEditGameDialog() {
      this.isEditGameDialogVisible = true
    },
    enablePulseClass() {
      this.pulseClass = true
    },
    disablePulseClass() {
      this.pulseClass = false
    },
    showRegionsToggleDialog() {
      this.isDialogVisible = true
    },
    closeRegionToggleDialog() {
      this.regions = []
      this.isDialogVisible = false
    },
    handleCommand(command) {
      switch (command) {
        case 'edit':
          this.showEditGameDialog()
          break
        case 'regions':
          this.showRegionsToggleDialog()
          break
        case 'deactivate':
          this.deactivateGame(this.game.id)
          break
      }
    },
    getActiveRegionsForGame(gameId) {
      const activeGame = this.$store.getters.activeGames.find(
        game => game.game_id === gameId
      )
      if (!activeGame) {
        this.activeGameId = null
        return []
      }
      this.activeGameId = activeGame.id
      return activeGame.regions || []
    }
  },
  computed: {
    isGameCatalogueRoute() {
      return this.$route.name === 'GamesHubCatalogue'
    },
    isGreyedOut() {
      return this.cardType === 'inactive'
    },
    getBackgroundImage() {
      if (this.isGreyedOut && !this.isGameCatalogueRoute) {
        return {
          backgroundImage: `linear-gradient(grey, grey), url(${this.game.image_url}`,
          backgroundBlendMode: 'saturation'
        }
      }

      return {
        backgroundImage: `url(${this.game.image_url}`
      }
    },
    isPulseEnabled() {
      return (
        this.pulseClass && this.cardType === 'inactive' && !$isRole('admin')
      )
    }
  }
}
</script>

<style scoped>
.card {
  width: 255px;
  cursor: pointer;
}

.card-ct {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 22px;
}

.img {
  width: 255px;
  height: 164px;
  background-repeat: no-repeat;
  background-size: cover;
}

.status-ct {
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
  padding-right: 10px;
}

.tag-ct {
  display: flex;
  align-items: center;
  gap: 4px;
}

.info-ct {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 22px;
  padding-right: 22px;
}

.game-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #4b515f;
  overflow-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  max-height: 54px;
}

.game-description {
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #7e8596;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  max-height: 54px;
}

.pulse {
  animation: pulse-animation 2s infinite;
}

@keyframes pulse-animation {
  0% {
    box-shadow: 0 0 0 0 rgba(29, 135, 200, 1);
  }
  100% {
    box-shadow: 0 0 0 20px rgba(0, 0, 0, 0);
  }
}

>>> .el-icon-more {
  transform: rotate(90deg);
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-hub-dashboard/GameCard.vue